// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

import { createClient } from "@supabase/supabase-js";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string;

// ── Password-recovery detection ─────────────────────────────────────────────
// We MUST read the URL HERE — at module evaluation time, before createClient()
// is called — because Supabase's createClient() calls
// window.history.replaceState() during initialisation to strip auth parameters
// from the URL.  By the time any React code or useEffect runs, the URL is gone.
//
// We also cannot rely on the PASSWORD_RECOVERY onAuthStateChange event alone:
// Supabase fires it during createClient() init before any listener is
// registered.  When our listener is registered later it only receives
// INITIAL_SESSION.  This module-level flag is the reliable fallback.
//
// Detection strategy (works for both PKCE and implicit flow):
//   PKCE link format:     https://domain/?recovery=1&code=XXXXX
//   Implicit link format: https://domain/?recovery=1#access_token=…&type=recovery
//
// The ?recovery=1 query param is appended to the redirectTo in resetPassword().
// It is readable here before Supabase strips it.
export const isPasswordRecoveryLoad =
  typeof window !== "undefined" &&
  (new URLSearchParams(window.location.search).get("recovery") === "1" ||
   window.location.hash.includes("type=recovery"));

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn("Supabase env vars not set — running in localStorage-only mode");
}

export const supabase = supabaseUrl && supabaseAnonKey
  ? createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        // PKCE flow (default). The code_verifier is stored in localStorage so
        // it survives opening the reset link in a new tab (unlike sessionStorage
        // which is tab-scoped and would break the exchange).
        flowType: "pkce",
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
      },
    })
  : null;
